import { Account, User } from '@mitel/cloudlink-sdk';
import { Subject } from 'rxjs';
import { IntegAzureAdSyncService } from '../services/integ-azure-ad-sync.service';
import { IntegOktaSyncService } from '../services/integ-okta-sync.service';
import { IntegGSuiteSyncService } from '../services/integ-gsuite-sync.service';
import { IntegGatewayService } from '../services/integ-gateway.service';
import { IntegMiteamMeetingsService } from '../services/integ-miteam-meetings.service';
import { IntegCallCenterService } from '../services/integ-call-center.service';
import { IntegWorkgroupsService } from '../services/integ-workgroups.service';
import { IntegVoiceAssistService } from '../services/integ-voice-assist.service';
import { IntegOffice365Service } from '../services/integ-office365.service';
import { IntegChatService } from '../services/integ-chat.service';
import { IntegMitelOneService } from '../services/integ-mitel-one.service';
import { IntegTelephonyService } from '../services/integ-telephony.service';
import { IntegSsoService } from '../services/integ-sso.service';
import { IntegFlexService } from '../services/integ-flex.service';
import { IntegMiVoiceBusinessService } from '../services/integ-mivoice-business.service';
import { IntegMitel5000Service } from '../services/integ-mitel-5000.service';
import { IntegPhonePresenceService } from '../services/integ-phone-presence.service';

import { StatusType, IntegrationStatusType } from '../constants/statusType.enum';
import { MenuItem } from './menu-item';
import { environment } from 'src/environments/environment';
import { UserPreFilterReasons } from '../constants/user-filter-constants';
import { IntegMSTeamsService } from '../services/integ-msteams.service';
import { IntegMiccService } from '../services/integ-micc.service';
import { IntegCustomerAdminPortalService } from '../services/integ-customer-admin-portal.service';
import { IntegTwilioService } from '../services/integ-twilio.service';
import { IntegCMcomService } from '../services/integ-cmcom.service';
import { IntegMSTeamsToCLPresenceService } from '../services/integ-msteams-to-cl-presence.service';
import { IntegWorkflowStudioService } from '../services/integ-workflow-studio.service';
import { IntegTwilioSmsNumbersService } from '../services/integ-twilio-sms-numbers.service';
import { IntegZappaPhoneService } from '../services/integ-zappa-phone.service';
import { IntegUnifyPhoneService } from '../services/integ-unify-phone.service';
import { IntegSipTrunksService } from '../services/integ-sip-trunks.service';
import { GenericFilterMenu } from './../../shared/components/generic-item-search-controls/generic-item-search-controls.component';

export interface Integration {
	displayName: string;
	description?: string;
	productCode: string;
	formControlId: string;
	roles: string[];
	data?: any;
	icon?: string;
	privileges?: string[];
	setupComplete?: boolean;
	oneTimeSetup?: boolean;
	/**
	 * When true, will add a config gear Icon to this Product's integration item component on the Account page
	 */
	requiresConfiguration: boolean;
	isDeletable: boolean;  // can this integration ever be deleted?
	usesConfigurationPage?: boolean;
	configurationDialog?: any;
	service?: IntegrationItemServiceInf
	configButtonLabel?: string;
	configurationPageUrl?: string;
	statusString?: string;
	setupBlocked?: boolean;
	incompatibleWith?: string[];
	freeTrial?: {
		eligible: boolean,
		granted: boolean
	};
	/**
	 * These are productCodes of other products that may be integrated along with the 'parent' product
	 * or, can be a configurable option/feature of the 'parent' product, eg MSTeamsToCLPresence.
	 */
	subIntegrationCodes?: string[];
	requires?: string[];
	/**
	 * When true, this indicates that IntegrationSectionComponent is still awiting
	 * integrationsService.initializeProductState() to complete
	 */
	loading?: boolean;
  }


export interface StatusString {
	status: string;
	type: StatusType;
};

export interface UserLicense {
	code: string;
	displayStringKey: string;
	// leaving this open for possible future requirements of multi level subs
};

export interface AuxFilterComponent {
	displayStr: string;
	phraseStr: string;
	id: string;
	component: any;
}

export enum IntegrationChangeType {
	NONE = 0,
	ENABLED_STATE = 1,
	DISABLED_STATE = 2,
	DIALOG_CLOSE = 3
}

// new requirement is that one integration or sub integration is going to cause a change
// in another integration or subintegration. We do not want the whole integration
// section to load. We only want the affected integration/ subfeature to update
export enum SpecificIntegrationActionType {
	REFRESH_ONLY = 0,
	ENABLE_AND_REFRESH = 1,
	DISABLE_AND_REFRESH = 2
}

export interface AuxActionData {
	statement: string;
	actionLabel: string;
	isReadOnly?: boolean //only set this to true if the aux dialog NEVER has edit/delete/create actions
}

export enum ExternalIntegrationActionType {
	UPDATE = 0,
	DELETE = 1
}

export interface ExternalIntegrationChange {
	action: ExternalIntegrationActionType;
	productCode: string;
}

export interface IntegrationItemServiceInf {
	canBeSelected?(account: Account): Promise<boolean>;
	canBeStandalone?(account: Account): Promise<boolean>;
	getConfigurationDialog(setupComplete?: boolean, user?: User): any;
	// we now have ONE instance of an integration where the "Complete Setup" button can invoke a different 
	// configuration dialog based on state of the integration. I do not want to combine the different behaviours into
	// the existing dialog, hence a need for this method. For now I will create a separate 
	// method for this unicorn. If this requirement starts being needed for other integrations this method
	// should be merged with getConfigurationDialog() which would need to have account as a paremeter and return a promise
	getDynamicConfigurationDialog?(account: Account): Promise<any>;
	isSetupComplete(account: Account) : Promise<boolean>;
	canSetupProceed?(account: Account): Promise<boolean>;
	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any>;
	getStatusString(account: Account): Promise<StatusString>;
	getConfigButtonLabel(account: Account): Promise<string>;
	isButtonDisabled(account: Account): Promise<boolean>;
	// the current conditions allow the integration to be deleted
	// not to be confused with "isDeletable" which means the integration is never to be deleted by the user
	canDeleteIntegration(account: Account): Promise<boolean>;
	getLeftNavMenus?(account: Account): Promise<MenuItem[]>
	getHighLevelStatus?(account: Account, haltStatusUpdateSubject: Subject<boolean>, tags?): Promise<any>;
	isIntegrationStateOn?(account: Account): Promise<boolean>;
	shouldNotifyParentOfChange?( account: Account, changeType: IntegrationChangeType ): Promise<any>;
	postCompleteIntegration?(account: Account): Promise<boolean>;
	isIntegrationStateChangeable?(account: Account): Promise<boolean>; // means that the integration or sub integration's state can/ cannot be changed (due to rbac, or policies, etc)
	canChangeIntegrationState?(account: Account, state: boolean): Promise<boolean>; // means at the point of the request, are the conditions OK to allow the change to continue (use confirmation dialog if necessary)
	getUserLicense?( user:User, account: Account ): Promise<UserLicense>;
	getUserEditComponent?( account: Account, single: boolean ): Promise<any>;
	initUserEditComponent?( componentInstance: any, users: User[], single: boolean );
	saveUserEditComponent?( componentInstance: any, users: User[], single: boolean ): Promise<any>;
	getEditUserDirtyEvent?( componentInstance: any, single: boolean ): any;
	getEditUserSaveEvent?( componentInstance: any, single: boolean ): any;
	isEligibleForFreeTrial?(account: Account): Promise<boolean>;
	grantFreeTrial?(account: Account, product: any): Promise<any>;
	handleAccountIntegrationChanges?(account: Account, tags?: any): Promise<void>;
	addIntegrationByDefault?(tags? :any): Promise<boolean>;
	getInfoLink?(account: Account):string;
	removeAsASubintegration?( account: Account ): Promise<any>;
	canConfigure?( account: Account ): Promise<any>; // OK it looks like the multiple flags don't cut is anymore, need to refactor at some point
	getAuxAction?( account: Account ): Promise<any>;
	handleAuxAction?( account: Account ): Promise<any>;
	handleEventFromAnotherIntegration?( account: Account, event: any ): Promise<any>; // Needed to push events to the child from parent but could be used elsewhere I guess
}
export interface IntegrationProductFilterInf {
	canFilterUsersOnProduct?( account: Account ): boolean;
	canPreFilterForCondition?( account: Account, reason: UserPreFilterReasons ): boolean;
	filterUsersBasedOnProduct?(account: Account, users: User[], filter:any[] ): Promise<User[]>; // TBD should filter by a typed object?
	getProductFilterMenu?(): GenericFilterMenu;
	getAuxFilterComponent?( account: Account ): Promise<AuxFilterComponent>;
	initAuxFilterComponent?( account: Account, componentInstance: any, filter:any[] ): any;
	getAuxFilterChangeEvent?( componentInstance: any ): any;
	isComponentStillValid?(account: Account, filter: any[]): Promise<boolean>;
	/**
	 * Test validity of the configuration.
	 * @param account 
	 * @returns true if the configuration is no longer valid
	 */
	needConfigUpdate?(account: Account): Promise<boolean>;
}

export interface IntegrationAsyncOperationInf {
	canHaveAsyncOperations( account: Account ): Promise<boolean>;
	isAsyncInProgress( account: Account ): Promise<boolean>;
	isDeleteAsync?( account: Account ): Promise<boolean>;
	// for each operation there could be a method?
	getAsyncJobIdsInProgress( account: Account ):Promise<string[]>;
	asyncJobIsComplete( account: Account, jobId: string );
	asyncJobFailed( account: Account, jobId: string, reason: string );
}

export const AccountBasedIntegrations: string[] = [
	'MiCC',
	'MiCollab',
	'MiTeam',
	'Gateway',
	'SSOAzure',
	'SyncAzure',
	'SyncOkta',
	'ContactCenter',
	'VoiceAssist',
	'SyncGSuite',
	'Office365',
	"chat",
	'MitelOne',
	'telephony',
	'MSTeams',
	'Workgroups',
	'Flex',
	'MiVoice5000',
	'phonePresence',
	'MiVB',
	'Twilio',
	'CMcom',
	'MSTeamsToCLPresence',
	'WorkflowStudio',
	'Twilio-sms-numbers',
	'Zappa',
	'UnifyPhone'
];

export const MitelIntegrations: Integration[] =  [
	{
		// Contact Center Admin
		displayName: "account.MiCCIntegration",
		productCode: "MiCC",
		formControlId: 'integration-micc',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		incompatibleWith: ['ContactCenter', 'Workgroups'],
		icon: '../../../../../assets/images/micc.svg',
		description: 'integrations.micc-config.int-description',
		privileges: ['auth', 'guest'],
		requiresConfiguration: false,
		isDeletable: true,
		service: new IntegMiccService()
	},
	{
		displayName:  "account.MiCollabIntegration",
		productCode: "MiCollab",
		formControlId: 'integration-micollab',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/mitel-generic-integration-icon.svg',
		description: 'integrations.micollab-config.int-description',
		privileges: ['auth'],
		requiresConfiguration: false,
		isDeletable: true,
		incompatibleWith: ['Flex']
	},
	{
		displayName:  "account.MiTeamIntegration",
		productCode: "MiTeam",
		formControlId: 'integration-miteam-meetings',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		incompatibleWith: ['MitelOne'],
		icon: '../../../../assets/images/miteam-meetings.svg',
		description: 'integrations.miteam-config.int-description',
		privileges: ['guest'],
		requiresConfiguration: true,
		oneTimeSetup: false,
		usesConfigurationPage: false,
		isDeletable: true,
		service: new IntegMiteamMeetingsService(),
	},
	{
		displayName:  "integrations.flex.integration-name",
		productCode: "Flex",
		formControlId: 'integration-flex',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/mitel-generic-integration-icon.svg',
		description: 'integrations.flex.int-description',
		isDeletable: true,
		requiresConfiguration: true,
		requires: ['MiTeam'],
		incompatibleWith: ['MiCollab'],
		oneTimeSetup: true,
		usesConfigurationPage: false,
		service: new IntegFlexService()
	},
	{
		displayName:  "account.Chat",
		productCode: "chat",
		formControlId: 'integration-chat',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/mitel-generic-integration-icon.svg',
		description: 'integrations.chat-config.int-description',
		requiresConfiguration: true,
		oneTimeSetup: false,
		usesConfigurationPage: false,
		isDeletable: false,
		service: new IntegChatService(),
	},
	{
		displayName:  "integrations.telephony.integration-name",
		productCode: "telephony",
		formControlId: 'integration-telephony',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/mitel-generic-integration-icon.svg',
		//description: 'integrations.chat-config.int-description',
		requiresConfiguration: false,
		oneTimeSetup: false,
		usesConfigurationPage: false,
		isDeletable: true,
		service: new IntegTelephonyService(),
	},
	{
		displayName: "integrations.gateway.integration-name",
		productCode: "Gateway",
		formControlId: 'integration-gateway',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/cl-gateway.svg',
		description: 'integrations.gateway.int-description',
		requiresConfiguration: true,
		usesConfigurationPage: true,
		configurationPageUrl: '/gateway',
		oneTimeSetup: false,
		isDeletable: true,
		service: new IntegGatewayService()
	},
	{
		displayName: "integrations.mitel-one.integration-name",
		productCode: "MitelOne",
		formControlId: 'integration-mitelone',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/mitel-one-icon.svg',
		//description: 'integrations.gateway.int-description',
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: false,
		isDeletable: true,
		service: new IntegMitelOneService(),
		subIntegrationCodes: ['chat', "MiTeam", "telephony"],
		privileges: ['guest']
	},
	{
		// MITEL ONE CX aka CCNEXT, aka CCN aka CX
		displayName: "integrations.cc-next.integration-name",
		productCode: "ContactCenter",
		formControlId: 'integration-ccnext',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/mitel-one-cx-icon.svg',
		description: 'users_multi_edit.cx_description',
		incompatibleWith: ['MiCC', 'Workgroups'],
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: true,
		isDeletable: true,
		service: new IntegCallCenterService(),
	},
	{
		displayName: "integrations.workgroups.integration-name",
		productCode: "Workgroups",
		formControlId: 'integration-workgroups',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/workgroups.png',
		incompatibleWith: ['MiCC', 'ContactCenter', 'VoiceAssist'],
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: true,
		isDeletable: true,
		service: new IntegWorkgroupsService(),
	},
	{
		displayName: "integrations.voice-assist.integration-name",
		productCode: "VoiceAssist",
		formControlId: 'integration-voiceassist',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/mitel-voice-assist-icon.svg',
		description: 'integrations.voice-assist.description',
		incompatibleWith: ['Workgroups'],
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: true,
		isDeletable: true,
		service: new IntegVoiceAssistService(),
	},
	{
		displayName:  "integrations.mivoice5000.integration-name",
		productCode: "MiVoice5000",
		formControlId: 'integration-mivoice-5000',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		incompatibleWith: ["MiVB"],
		icon: '../../../../assets/images/5000.png',
		description: 'integrations.mivoice5000.int-description',
		requiresConfiguration: false,
		oneTimeSetup: false,
		usesConfigurationPage: false,
		isDeletable: true,
		subIntegrationCodes: ['chat'],
		service: new IntegMitel5000Service(),
	},
	{
		displayName:  "integrations.phonePresence.integration-name",
		productCode: "phonePresence",
		formControlId: 'integration-phonepresence',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/mitel-generic-integration-icon.svg',
		requiresConfiguration: false,
		oneTimeSetup: false,
		usesConfigurationPage: false,
		isDeletable: false,
		service: new IntegPhonePresenceService(),
	},
	{
		displayName:  "integrations.MSTeamsToCLPresence.integration-name",
		productCode: "MSTeamsToCLPresence",
		formControlId: 'integration-MSTeamsToCLPresence',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/mitel-generic-integration-icon.svg',
		requiresConfiguration: true,
		oneTimeSetup: false,
		usesConfigurationPage: false,
		isDeletable: false,
		service: new IntegMSTeamsToCLPresenceService(),
	},
	{
		displayName:  "integrations.mivb.integration-name",
		productCode: "MiVB",
		formControlId: 'integration-mivb',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/mitel-generic-integration-icon.svg',
		description: 'integrations.mivb.int-description',
		isDeletable: true,
		requiresConfiguration: false,
		incompatibleWith: ["MiVoice5000"],
		oneTimeSetup: false,
		usesConfigurationPage: false,
		service: new IntegMiVoiceBusinessService(),
		subIntegrationCodes: ['chat', "MiTeam", 'CustomerAdminPortal'],
	},
	{
		displayName:  "integrations.customerAdminPortal.integration-name",
		productCode: "CustomerAdminPortal",
		formControlId: 'integration-customerAdminPortal',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/mitel-generic-integration-icon.svg',
		isDeletable: true,
		requiresConfiguration: false,
		oneTimeSetup: false,
		usesConfigurationPage: false,
		service: new IntegCustomerAdminPortalService()
	},
	{
		displayName: "integrations.workflow-studio.integration-name",
		productCode: "WorkflowStudio",
		formControlId: 'integration-workflow-studio',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		description: "integrations.workflow-studio.description",
		icon: '../../../../assets/images/workflow-studio-logo.svg',
		requiresConfiguration: false,
		usesConfigurationPage: false,
		oneTimeSetup: false,
		isDeletable: true,
		service: new IntegWorkflowStudioService()
	},
	{
		displayName: 'integrations.unify-phone.name',
		productCode: 'UnifyPhone',
		formControlId: 'integration-unify-phone',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		description: 'integrations.unify-phone.description',
		icon: '../../../../assets/images/unify-phone.png',
		privileges: ['auth'],
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: false,
		isDeletable: true,
		service: new IntegUnifyPhoneService(),
		subIntegrationCodes: ['SIPTrunks']
	},
	{
		displayName: 'integrations.sip-trunks.name',
		productCode: 'SIPTrunks',
		formControlId: 'integration-sip-trunks',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		icon: '../../../../assets/images/mitel-generic-integration-icon.svg',
		requiresConfiguration: true,
		oneTimeSetup: false,
		usesConfigurationPage: false,
		isDeletable: false,
		service: new IntegSipTrunksService()
	}
];

export const ThirdPartyIntegrations: Integration[] =  [
	{
		displayName: "integrations.sso.integration-name",
		productCode: "SSOAzure",
		formControlId: 'integration-sso',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		description: "integrations.sso.int-description",
		icon: '../../../../assets/images/sso.svg',
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: false,
		isDeletable: true,
		service: new IntegSsoService()
	},
	{
		displayName: "Azure AD Sync",
		productCode: "SyncAzure",
		formControlId: 'integration-syncazure',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		description: "integrations.azure-sync.int-description",
		icon: '../../../../assets/images/azure.png',
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: false,
		isDeletable: true,
		incompatibleWith: ['SyncOkta', 'SyncGSuite'],
		service: new IntegAzureAdSyncService()
	},
	{
		displayName: "OKTA Sync",
		productCode: "SyncOkta",
		formControlId: 'integration-syncokta',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		description: "integrations.okta-sync.int-description",
		icon: '../../../../assets/images/okta.png',
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: false,
		isDeletable: true,
		incompatibleWith: ['SyncAzure', 'SyncGSuite'],
		service: new IntegOktaSyncService()
	},
	{
		displayName: "Google Workspace Sync",
		productCode: "SyncGSuite",
		formControlId: 'integration-syncgsuite',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		description: "integrations.gsuite-sync.int-description",
		icon: '../../../../assets/images/Google_G_Logo.png',
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: false,
		isDeletable: true,
		incompatibleWith: ['SyncOkta', 'SyncAzure'],
		service: new IntegGSuiteSyncService()
	},
	{
		displayName: "Microsoft Office 365",
		productCode: "Office365",
		formControlId: 'integration-office365',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		description: "integrations.office365.int-description",
		icon: '../../../../assets/images/office365.png',
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: false,
		isDeletable: true,
		service: new IntegOffice365Service()
	},
	{
		displayName: "Microsoft Teams",
		productCode: "MSTeams",
		formControlId: 'integration-msteams',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		description: "integrations.msteams.description",
		icon: '../../../../assets/images/msteams.png',
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: false,
		isDeletable: true,
		service: new IntegMSTeamsService(),
		subIntegrationCodes: ['phonePresence', 'MSTeamsToCLPresence']
	},
	{
		displayName: "Twilio",
		productCode: "Twilio",
		formControlId: 'integration-twilio',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		description: "integrations.twilio.int-description",
		icon: '../../../../assets/images/twilio.svg',
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: false,
		isDeletable: true,
		service: new IntegTwilioService(),
		subIntegrationCodes: [], // Note that feature-flags may add sub integrations, like 'Twilio-sms-numbers'. See IntegrationService.getThirdPartyIntegrations().
	},
	{
		displayName:  "integrations.twilio.sms-numbers",
		productCode: "Twilio-sms-numbers",
		formControlId: 'integration-twilio-sms-numbers',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		requiresConfiguration: false,
		oneTimeSetup: false,
		usesConfigurationPage: false,
		isDeletable: true,
		service: new IntegTwilioSmsNumbersService(),
	},
	{
		displayName: "CM.com",
		productCode: "CMcom",
		formControlId: 'integration-cmcom',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		description: "integrations.cmcom.int-description",
		icon: '../../../../assets/images/cmcom.png',
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: false,
		isDeletable: true,
		service: new IntegCMcomService()
	},
	{
		displayName: "Zoom",
		productCode: "Zappa",
		formControlId: 'integration-zappa',
		roles:[
			'PARTNER_ADMIN',
			'ACCOUNT_ADMIN'
		],
		description: "integrations.zappa.int-description",
		icon: '../../../../../assets/images/zappa.png',  // investigation required to find a product icon
		requiresConfiguration: true,
		usesConfigurationPage: false,
		oneTimeSetup: false,
		isDeletable: true,
		service: new IntegZappaPhoneService()
	}
];

export interface Privilege {
	displayName: string;
	productCode: string;
	formControlId: string;
	data?: any;
	learnMoreLink?: string;
  }

export const GeneralPrivileges: Privilege[] =  [
	{
		displayName: "account.delegate_auth",
		productCode: "auth",
		formControlId: 'privilege-auth',
		learnMoreLink:"integrations.mitel-help.privileges"
	},
	{
		displayName: "account.guestAccess",
		productCode: "guest",
		formControlId: 'privilege-guest',
		learnMoreLink:"integrations.mitel-help.privileges"
	}
];

export const IntegToLicenseCrossreference =
[
	{
		integProductCode: "MiTeam",
		licenseNameInBilling: environment.subscriptionIds.MiTeam
	},
	{
		integProductCode: "ContactCenter",
		licenseNameInBilling: environment.subscriptionIds.ContactCenter
	},
	{
		integProductCode: "MitelOne",
		licenseNameInBilling: environment.subscriptionIds.MitelOne
	},
	{
		integProductCode: "MiVB",
		licenseNameInBilling: environment.subscriptionIds.MiVoiceBusiness
	},
	{
		integProductCode: "WorkflowStudio",
		licenseNameInBilling: environment.subscriptionIds.WorkflowStudio
	},
	{
		integProductCode: "Zappa",
		licenseNameInBilling: environment.subscriptionIds.ZoomHybrid
	},
]


export interface IntegrationHighLevelStatus {
	state: IntegrationStatusType;
	statusDisplayString: string;
}

export interface IntegrationStatus {
	productCode: string;
	integNameStatusDisplayString: string;
	status: IntegrationHighLevelStatus;
}
